import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';

export function cn(...values: (string | false | null | undefined)[]): string {
  return values.filter(Boolean).join(' ');
}

/** Zahl der Overlays, die den Hintergrund gerade sperren, und wie er danach wieder aussieht. */
let scrollLocks = 0;
let releaseScrollLock: (() => void) | null = null;

/**
 * Sperrt den Hintergrund, solange ein Overlay geoeffnet ist.
 *
 * `overflow: hidden` allein genuegt auf Mobilgeraeten nicht - erst die feste
 * Positionierung verhindert, dass die Seite hinter dem Overlay mitscrollt.
 * Die vorherige Scrollposition wird beim Schliessen wiederhergestellt.
 *
 * Liegt ein Overlay ueber einem anderen (Vollbildskizze im Dialog), gilt die Sperre
 * einmal: Erst das letzte Schliessen stellt die Seite wieder her - gleich in welcher
 * Reihenfolge die Overlays verschwinden.
 */
export function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    if (scrollLocks === 0) {
      const scrollY = window.scrollY;
      const { body } = document;
      const previous = {
        position: body.style.position,
        top: body.style.top,
        width: body.style.width,
        overflow: body.style.overflow,
      };
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      releaseScrollLock = () => {
        body.style.position = previous.position;
        body.style.top = previous.top;
        body.style.width = previous.width;
        body.style.overflow = previous.overflow;
        window.scrollTo(0, scrollY);
      };
    }
    scrollLocks += 1;

    return () => {
      scrollLocks -= 1;
      if (scrollLocks === 0) {
        releaseScrollLock?.();
        releaseScrollLock = null;
      }
    };
  }, [active]);
}

/* ------------------------------------------------------------------ Button */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white shadow-sm hover:bg-brand-700 disabled:bg-brand-300',
  secondary:
    'bg-white text-slate-700 border border-slate-300 shadow-sm hover:bg-slate-50 hover:border-slate-400 disabled:text-slate-400',
  ghost: 'bg-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900',
  danger: 'bg-rose-600 text-white shadow-sm hover:bg-rose-700 disabled:bg-rose-300',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'min-h-11 px-3 py-1.5 text-sm sm:min-h-0',
  md: 'min-h-11 px-4 py-2.5 text-sm',
  lg: 'px-5 py-3 text-base',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps): JSX.Element {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-all active:scale-[0.98]',
        'disabled:cursor-not-allowed',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        className,
      )}
    >
      {loading && <Spinner className="h-4 w-4" />}
      {children}
    </button>
  );
}

export function Spinner({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      className={cn('animate-spin', className ?? 'h-5 w-5')}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      />
    </svg>
  );
}

/* ------------------------------------------------------------- Formularteile */

export interface FieldProps {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: (id: string) => ReactNode;
}

export function Field({ label, hint, error, required, children }: FieldProps): JSX.Element {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="label">
        {label}
        {required && <span className="ml-0.5 text-rose-600">*</span>}
      </label>
      {children(id)}
      {hint && !error && <p className="hint">{hint}</p>}
      {error && <p className="error-text">{error}</p>}
    </div>
  );
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>): JSX.Element {
  return <input {...props} className={cn('input', props.className)} />;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>): JSX.Element {
  return <textarea {...props} className={cn('input', props.className)} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>): JSX.Element {
  return <select {...props} className={cn('input', props.className)} />;
}

export function Checkbox({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
      />
      <span>{label}</span>
    </label>
  );
}

/* -------------------------------------------------------------------- Karte */

export function Card({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return <div className={cn('card', className)}>{children}</div>;
}

export function CardBody({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return <div className={cn('card-body', className)}>{children}</div>;
}

export function SectionTitle({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}): JSX.Element {
  return (
    <div className="mb-3 flex items-start justify-between gap-3">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

/* ------------------------------------------------------------------ Abzeichen */

type BadgeTone = 'neutral' | 'brand' | 'success' | 'warning' | 'orange' | 'danger' | 'info';

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'bg-slate-100 text-slate-700 ring-slate-200',
  brand: 'bg-brand-50 text-brand-700 ring-brand-200',
  success: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  warning: 'bg-amber-50 text-amber-800 ring-amber-200',
  // Deutlich kraeftiger als `warning`, damit die drei Betragsklassen
  // gruen/gelb/orange nebeneinander unterscheidbar bleiben.
  orange: 'bg-orange-100 text-orange-900 ring-orange-300',
  danger: 'bg-rose-50 text-rose-700 ring-rose-200',
  info: 'bg-sky-50 text-sky-700 ring-sky-200',
};

export function Badge({
  children,
  tone = 'neutral',
  className,
  title,
}: {
  children: ReactNode;
  tone?: BadgeTone;
  className?: string;
  title?: string;
}): JSX.Element {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        BADGE_TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------- Dialog */

/** Geoeffnete Dialoge (und Vollbildansichten) in der Reihenfolge des Oeffnens - Escape gilt nur dem obersten. */
const openDialogs: symbol[] = [];

/**
 * Escape schliesst nur das oberste geoeffnete Overlay. Liegt eines ueber einem anderen
 * (etwa die Auswahl im Formular oder die Vollbildskizze im Uebungsdetail), bleibt das
 * untere offen. Die Reihenfolge haengt nur an `open`, nicht an der Identitaet von `onClose`.
 */
export function useEscapeToClose(open: boolean, onClose: () => void): void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const token = useRef(Symbol('dialog'));
  useEffect(() => {
    if (!open) return;
    const me = token.current;
    openDialogs.push(me);
    const handler = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && openDialogs[openDialogs.length - 1] === me) onCloseRef.current();
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
      const index = openDialogs.indexOf(me);
      if (index >= 0) openDialogs.splice(index, 1);
    };
  }, [open]);
}

export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}): JSX.Element | null {
  useScrollLock(open);
  useEscapeToClose(open, onClose);

  if (!open) return null;

  /*
   * Der Dialog wird bewusst an `document.body` gehaengt.
   *
   * `position: fixed` bezieht sich sonst nicht auf das Browserfenster, sobald
   * ein Vorfahre `filter`, `transform` oder `backdrop-filter` gesetzt hat -
   * solche Elemente werden selbst zum Bezugsrahmen. Die Kopfzeile verwendet
   * `backdrop-blur`; ohne Portal landete jeder darin geoeffnete Dialog
   * (Bereichsauswahl, Suche, Benachrichtigungen) ausserhalb des Bildschirms.
   */
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div
        className="absolute inset-0 bg-slate-900/40"
        onClick={onClose}
        role="presentation"
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          'animate-fade relative flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl',
          'sm:rounded-2xl',
          wide ? 'sm:max-w-3xl' : 'sm:max-w-lg',
        )}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="flex min-h-11 min-w-11 items-center justify-center rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M6.3 5A1 1 0 004.9 6.4L8.6 10l-3.7 3.6a1 1 0 101.4 1.4l3.7-3.6 3.6 3.6a1 1 0 001.4-1.4L11.4 10l3.6-3.6a1 1 0 00-1.4-1.4L10 8.6 6.3 5z" />
            </svg>
          </button>
        </div>
        {/* min-h-0 ist noetig, damit der Inhalt scrollt statt die Fusszeile
            aus dem sichtbaren Bereich zu draengen (Flexbox-Standardverhalten). */}
        <div data-dialog-body className="min-h-0 flex-1 overflow-y-auto px-4">
          {/* Der senkrechte Innenrand liegt innen, damit klebende Leisten (Reiter) buendig am oberen Rand des Scrollbereichs sitzen. */}
          <div className="py-4">{children}</div>
        </div>
        {footer && (
          <div className="flex shrink-0 justify-end gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/* ------------------------------------------------------------ Leere Zustaende */

export function EmptyState({
  title,
  description,
  action,
  actions,
  note,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  /** Weitere Knöpfe neben `action`, z. B. „Filter zurücksetzen“. */
  actions?: ReactNode;
  /** Kleiner Hinweis unter der Beschreibung, z. B. der gewählte Bereich. */
  note?: ReactNode;
}): JSX.Element {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
      <p className="font-medium text-slate-700">{title}</p>
      {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
      {note && <p className="mt-2 text-sm text-slate-600">{note}</p>}
      {(action || actions) && (
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          {action}
          {actions}
        </div>
      )}
    </div>
  );
}

export function LoadingBlock({ label = 'Wird geladen …' }: { label?: string }): JSX.Element {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
      <Spinner className="h-4 w-4" />
      {label}
    </div>
  );
}

export function ErrorBlock({ error }: { error: unknown }): JSX.Element {
  const message =
    error instanceof Error ? error.message : 'Die Daten konnten nicht geladen werden.';
  return (
    <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
      {message}
    </div>
  );
}

/* --------------------------------------------------------------------- Tabs */

export function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: string; label: string; badge?: number }[];
  active: string;
  onChange: (id: string) => void;
}): JSX.Element {
  return (
    // Am Handy brechen die Reiter um statt seitlich zu verschwinden - ein
    // abgeschnittener Reiter wird nicht gefunden.
    <div className="mb-4 sm:overflow-x-auto">
      <div className="flex flex-wrap gap-x-1 border-b border-slate-200 sm:min-w-max sm:flex-nowrap">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(tab.id)}
            className={cn(
              'whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              active === tab.id
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-slate-500 hover:text-slate-700',
            )}
          >
            {tab.label}
            {tab.badge !== undefined && tab.badge > 0 && (
              <span className="ml-1.5 rounded-full bg-brand-100 px-1.5 py-0.5 text-xs text-brand-800">
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------- Kurzmeldungen (Toast) */

interface Toast {
  id: number;
  message: string;
  tone: 'success' | 'error';
}

interface ToastContextValue {
  show: (message: string, tone?: 'success' | 'error') => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const show = (message: string, tone: 'success' | 'error' = 'success'): void => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { id, message, tone }]);
    window.setTimeout(() => {
      setToasts((current) => current.filter((t) => t.id !== id));
    }, 4000);
  };

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-20 z-[60] flex flex-col items-center gap-2 px-4 sm:bottom-6">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            role="status"
            className={cn(
              'animate-fade pointer-events-auto max-w-md rounded-lg px-4 py-2.5 text-sm shadow-lg',
              toast.tone === 'success' ? 'bg-slate-900 text-white' : 'bg-rose-600 text-white',
            )}
          >
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/** Wie `useToast`, aber ohne Fehler, wenn kein ToastProvider da ist (Bausteine, die auch allein gezeigt werden). */
export function useOptionalToast(): ToastContextValue | null {
  return useContext(ToastContext);
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast muss innerhalb von ToastProvider verwendet werden');
  return context;
}
